import {
  FC,
  useEffect,
  useRef,
  useState,
  type RefObject,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SwipeOutlined from '@mui/icons-material/SwipeOutlined';
import { styled } from '@mui/material/styles';
import Row from '../Layout/Row';

const ScrollDecorRoot = styled(Box)(({ theme }) => ({
  width: '100%',
  flexShrink: 0,
  marginTop: theme.spacing(theme.spacingSizes.xl),
  padding: theme.spacing(theme.spacingSizes.xs),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(theme.spacingSizes.lg),
  },
}));

const ScrollbarTrack = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '10px',
  backgroundColor: theme.palette.grey[100],
  borderRadius: '100px',
  cursor: 'pointer',
}));

const ScrollbarThumb = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.grey[900],
  border: `2px solid ${theme.palette.grey[100]}`,
  borderRadius: '100px',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
}));

const SwipeHintRow = styled(Row)(({ theme }) => ({
  marginTop: theme.spacing(theme.spacingSizes.xxs * 3),
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

const SwipeIcon = styled(SwipeOutlined)(({ theme }) => ({
  fontSize: theme.spacing(theme.spacingSizes.sm),
  color: theme.palette.grey[400],
}));

type ScrollMetrics = {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
};

type Props = {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  showSwipeHint?: boolean;
  swipeHintText?: string;
};

const HorizontalScrollDecor: FC<Props> = ({
  scrollContainerRef,
  contentRef,
  showSwipeHint = false,
  swipeHintText = 'Swipe right to see more',
}) => {
  const [metrics, setMetrics] = useState<ScrollMetrics | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const didDragRef = useRef(false);

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const contentEl = contentRef.current;
    if (scrollEl == null || contentEl == null) {
      return undefined;
    }

    const update = () => {
      const scroll = scrollContainerRef.current;
      const content = contentRef.current;

      if (scroll == null || content == null) {
        return;
      }

      const isContentOverflowing =
        Math.ceil(content.offsetWidth) > Math.floor(scroll.clientWidth);

      if (!isContentOverflowing) {
        setMetrics(null);
        return;
      }
      setMetrics({
        scrollLeft: scroll.scrollLeft,
        scrollWidth: scroll.scrollWidth,
        clientWidth: scroll.clientWidth,
      });
    };

    update();
    scrollEl.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(scrollEl);
    resizeObserver.observe(contentEl);
    return () => {
      scrollEl.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef, contentRef]);

  if (metrics == null) {
    return null;
  }

  const maxScroll = metrics.scrollWidth - metrics.clientWidth;
  const thumbWidthPercent = (metrics.clientWidth / metrics.scrollWidth) * 100;
  const thumbLeftPercent =
    maxScroll > 0
      ? (metrics.scrollLeft / maxScroll) * (100 - thumbWidthPercent)
      : 0;

  const handleTrackClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (didDragRef.current) {
      return;
    }
    const scrollEl = scrollContainerRef.current;
    if (scrollEl == null) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    scrollEl.scrollLeft = ratio * maxScroll;
  };

  const handleThumbMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = false;

    const scrollEl = scrollContainerRef.current;
    const trackEl = trackRef.current;
    if (scrollEl == null || trackEl == null || maxScroll <= 0) {
      return;
    }

    const trackWidth = trackEl.getBoundingClientRect().width;
    const thumbTravel = trackWidth * (1 - thumbWidthPercent / 100);
    const startX = event.clientX;
    const startScrollLeft = scrollEl.scrollLeft;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (Math.abs(moveEvent.clientX - startX) > 2) {
        didDragRef.current = true;
      }
      const deltaX = moveEvent.clientX - startX;
      const scrollDelta =
        thumbTravel > 0 ? (deltaX / thumbTravel) * maxScroll : 0;
      scrollEl.scrollLeft = Math.min(
        maxScroll,
        Math.max(0, startScrollLeft + scrollDelta),
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.setTimeout(() => {
        didDragRef.current = false;
      }, 0);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <ScrollDecorRoot>
      <ScrollbarTrack
        ref={trackRef}
        onClick={handleTrackClick}
        role="presentation"
      >
        <ScrollbarThumb
          style={{
            width: `${thumbWidthPercent}%`,
            left: `${thumbLeftPercent}%`,
          }}
          onMouseDown={handleThumbMouseDown}
        />
      </ScrollbarTrack>
      {showSwipeHint ? (
        <SwipeHintRow justify="space-between" centered>
          <Typography variant="caption" color="grey.500">
            {swipeHintText}
          </Typography>
          <SwipeIcon />
        </SwipeHintRow>
      ) : null}
    </ScrollDecorRoot>
  );
};

export default HorizontalScrollDecor;
